import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Search, AlertTriangle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { api, API } from "@/lib/api";
import { STATUS, STATUS_KEYS, addDays, fmtDate, mondayOf, todayTR } from "@/lib/helpers";
import { AdminTitle } from "@/components/admin/AdminLayout";
import { WeekCalendar } from "@/components/admin/WeekCalendar";
import { AppointmentList } from "@/components/admin/AppointmentList";
import { AppointmentDialog } from "@/components/admin/AppointmentDialog";
import { CleanupDialog } from "@/components/admin/CleanupDialog";

function StatusCards({ items, filter, setFilter }) {
  const conflicts = items.filter((a) => a.conflict).length;
  const card = (key, label, count, cls) => (
    <button key={key} onClick={() => setFilter(filter === key ? "" : key)} data-testid={`status-card-${key}`}
      className={`rounded-2xl border p-4 text-left transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md ${cls} ${filter === key ? "ring-2 ring-navy" : ""}`}>
      <p className="text-xs font-bold uppercase tracking-wider opacity-80">{label}</p>
      <p className="mt-1 font-heading text-3xl font-black" data-testid={`status-count-${key}`}>{count}</p>
    </button>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
      {STATUS_KEYS.map((s) => card(s, STATUS[s].label, items.filter((a) => a.status === s).length, STATUS[s].badge))}
      {card("conflict", "Çakışma", conflicts, conflicts ? "bg-red-600 text-white border-red-700" : "bg-white text-slate-700 border-bej")}
    </div>
  );
}

export default function Appointments() {
  const [week, setWeek] = useState(mondayOf(todayTR()));
  const [view, setView] = useState("takvim");
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [qd, setQd] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [academy, setAcademy] = useState("Masal Sanat Akademi");
  const end = addDays(week, 6);

  useEffect(() => {
    const t = setTimeout(() => setQd(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    api.get("/public/site").then((r) => setAcademy(r.data.academy_name)).catch(() => {});
  }, []);
  const load = useCallback(() => {
    api.get("/admin/appointments", { params: qd ? { q: qd } : { start: week, end } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [week, end, qd]);
  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => items.filter((a) => !filter || (filter === "conflict" ? a.conflict : a.status === filter)), [items, filter]);
  const onChanged = (updated) => {
    setSelected(updated);
    load();
  };

  return (
    <div data-testid="admin-appointments-page">
      <AdminTitle title="Randevular">
        <CleanupDialog onDone={load} />
        <a href={`${API}/admin/export.csv?start=${week}&end=${end}`} data-testid="export-week-csv"
          className="inline-flex items-center gap-2 rounded-full bg-white border border-bej px-4 py-2 text-sm font-bold text-navy hover:bg-bej-light">
          <Download className="w-4 h-4" /> Bu hafta CSV
        </a>
        <a href={`${API}/admin/export.csv`} data-testid="export-all-csv"
          className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-bold text-white hover:bg-navy-dark">
          <Download className="w-4 h-4" /> Tümü CSV
        </a>
      </AdminTitle>
      <StatusCards items={items} filter={filter} setFilter={setFilter} />
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => setWeek(addDays(week, -7))} className="p-2 rounded-full bg-white border border-bej hover:bg-bej-light" data-testid="week-prev-btn" aria-label="Önceki hafta"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setWeek(mondayOf(todayTR()))} className="rounded-full bg-white border border-bej px-4 py-2 text-sm font-bold text-navy hover:bg-bej-light" data-testid="week-today-btn">Bu hafta</button>
          <button onClick={() => setWeek(addDays(week, 7))} className="p-2 rounded-full bg-white border border-bej hover:bg-bej-light" data-testid="week-next-btn" aria-label="Sonraki hafta"><ChevronRight className="w-4 h-4" /></button>
          <span className="ml-2 font-heading font-bold text-navy" data-testid="week-label">
            {qd ? "Arama sonuçları" : `${fmtDate(week, { day: "numeric", month: "short" })} – ${fmtDate(end, { day: "numeric", month: "short", year: "numeric" })}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, telefon, talep no…" className="pl-9 rounded-full w-60 bg-white" data-testid="appointment-search-input" />
          </div>
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="rounded-full bg-bej">
              <TabsTrigger value="takvim" className="rounded-full" data-testid="view-calendar-tab">Takvim</TabsTrigger>
              <TabsTrigger value="liste" className="rounded-full" data-testid="view-list-tab">Liste</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      {items.some((a) => a.conflict) && (
        <p className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 border border-red-300 px-4 py-2.5 text-sm font-bold text-red-700" data-testid="conflict-banner">
          <AlertTriangle className="w-4 h-4" /> Aynı saate düşen talepler kırmızıyla işaretlendi. Karar sizde: birini onaylayın, diğerinin saatini değiştirin.
        </p>
      )}
      {view === "takvim" && !qd ? (
        <WeekCalendar week={week} items={shown} onSelect={setSelected} />
      ) : (
        <AppointmentList items={shown} onSelect={setSelected} academy={academy} />
      )}
      <AppointmentDialog appt={selected} academy={academy} onClose={() => setSelected(null)} onChanged={onChanged} />
    </div>
  );
}
